/**
 * Admin: Persona Sharing page.
 *
 * Allows admins to share their personas with specific users.
 * Shows all personas owned by the current admin, with the ability
 * to view/add/remove shares for each persona.
 */

import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/services/apiClient'
import {
  Button,
  Badge,
  Alert,
  AlertDescription,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Spinner,
} from '@/components/ui'
import {
  Share2,
  UserPlus,
  UserMinus,
  Users,
  ChevronDown,
  ChevronRight,
  Check,
} from 'lucide-react'

interface Persona {
  id: string
  name: string
  description: string | null
  presentation_mode: string
}

interface PersonaShareInfo {
  id: string
  persona_id: string
  user_id: string
  user_email: string
  user_display_name: string
  shared_by: string | null
  created_at: string | null
}

interface UserSummary {
  id: string
  email: string
  display_name: string
  role: string
  is_active: boolean
}

export function PersonaSharingPage() {
  const [personas, setPersonas] = useState<Persona[]>([])
  const [users, setUsers] = useState<UserSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Expanded persona (show shares inline)
  const [expandedPersonaId, setExpandedPersonaId] = useState<string | null>(null)
  const [shares, setShares] = useState<Record<string, PersonaShareInfo[]>>({})
  const [sharesLoading, setSharesLoading] = useState<Record<string, boolean>>({})

  // Share dialog
  const [sharePersona, setSharePersona] = useState<Persona | null>(null)
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [isSharing, setIsSharing] = useState(false)

  // Unshare loading
  const [unshareLoading, setUnshareLoading] = useState<Record<string, boolean>>({})

  const fetchPersonas = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/personas')
      if (!res.ok) throw new Error('Failed to fetch personas')
      const data = await res.json()
      setPersonas(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load personas')
    }
  }, [])

  const fetchUsers = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/admin/users')
      if (!res.ok) throw new Error('Failed to fetch users')
      const data = await res.json()
      setUsers(data.filter((u: UserSummary) => u.is_active))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    }
  }, [])

  useEffect(() => {
    Promise.all([fetchPersonas(), fetchUsers()]).finally(() => setIsLoading(false))
  }, [fetchPersonas, fetchUsers])

  // ── Load shares for a persona ──

  const loadShares = useCallback(async (personaId: string) => {
    setSharesLoading((prev) => ({ ...prev, [personaId]: true }))
    try {
      const res = await apiClient.fetch(`/api/admin/personas/${personaId}/shares`)
      if (!res.ok) throw new Error('Failed to load shares')
      const data = await res.json()
      setShares((prev) => ({ ...prev, [personaId]: data }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load shares')
    } finally {
      setSharesLoading((prev) => ({ ...prev, [personaId]: false }))
    }
  }, [])

  const toggleExpand = (personaId: string) => {
    if (expandedPersonaId === personaId) {
      setExpandedPersonaId(null)
    } else {
      setExpandedPersonaId(personaId)
      if (!shares[personaId]) {
        loadShares(personaId)
      }
    }
  }

  // ── Share persona with users ──

  const openShareDialog = (persona: Persona) => {
    setSharePersona(persona)
    setSelectedUserIds(new Set())
  }

  const handleShare = async () => {
    if (!sharePersona || selectedUserIds.size === 0) return
    setIsSharing(true)

    try {
      const res = await apiClient.fetch(`/api/admin/personas/${sharePersona.id}/shares`, {
        method: 'POST',
        body: JSON.stringify({ user_ids: Array.from(selectedUserIds) }),
      })
      if (!res.ok) throw new Error('Failed to share persona')
      setSharePersona(null)
      setSelectedUserIds(new Set())
      // Refresh shares for this persona
      await loadShares(sharePersona.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share persona')
    } finally {
      setIsSharing(false)
    }
  }

  // ── Unshare ──

  const handleUnshare = async (personaId: string, userId: string) => {
    const key = `${personaId}-${userId}`
    setUnshareLoading((prev) => ({ ...prev, [key]: true }))
    try {
      const res = await apiClient.fetch(`/api/admin/personas/${personaId}/shares/${userId}`, {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error('Failed to unshare')
      await loadShares(personaId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unshare persona')
    } finally {
      setUnshareLoading((prev) => ({ ...prev, [key]: false }))
    }
  }

  // ── Helpers ──

  const getAlreadySharedUserIds = (personaId: string): Set<string> => {
    const personaShares = shares[personaId] || []
    return new Set(personaShares.map((s) => s.user_id))
  }

  const toggleUserSelection = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        next.add(userId)
      }
      return next
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner className="w-8 h-8" />
      </div>
    )
  }

  return (
    <PageFrame width="form" className="space-y-6">
      <PageHeader
        title="Persona Sharing"
        subtitle="Share personas with users. Shared personas are read-only for recipients."
      />

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {personas.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p>No personas found. Create a persona first to share it.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {personas.map((persona) => {
            const isExpanded = expandedPersonaId === persona.id
            const personaShares = shares[persona.id] || []
            const isLoadingShares = sharesLoading[persona.id]

            return (
              <div
                key={persona.id}
                className="bg-background border border-border rounded-lg overflow-hidden"
              >
                {/* Persona header row */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-muted"
                  onClick={() => toggleExpand(persona.id)}
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {persona.name}
                    </div>
                    {persona.description && (
                      <div className="text-xs text-muted-foreground truncate">
                        {persona.description}
                      </div>
                    )}
                  </div>
                  <Badge variant="secondary" className="shrink-0">
                    {persona.presentation_mode}
                  </Badge>
                  {personaShares.length > 0 && (
                    <Badge variant="default" className="shrink-0">
                      {personaShares.length} shared
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    title="Share with users"
                    onClick={(e) => {
                      e.stopPropagation()
                      openShareDialog(persona)
                    }}
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Expanded: show shares */}
                {isExpanded && (
                  <div className="border-t border-border px-4 py-3 bg-muted">
                    {isLoadingShares ? (
                      <div className="flex items-center justify-center py-4">
                        <Spinner />
                      </div>
                    ) : personaShares.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-2">
                        Not shared with anyone.{' '}
                        <button
                          className="text-primary hover:underline"
                          onClick={() => openShareDialog(persona)}
                        >
                          Share now
                        </button>
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Shared with
                        </p>
                        {personaShares.map((share) => {
                          const unshareKey = `${share.persona_id}-${share.user_id}`
                          return (
                            <div
                              key={share.id}
                              className="flex items-center justify-between py-1.5"
                            >
                              <div>
                                <span className="text-sm font-medium text-foreground">
                                  {share.user_display_name}
                                </span>
                                <span className="text-xs text-muted-foreground ml-2">
                                  {share.user_email}
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Remove share"
                                disabled={unshareLoading[unshareKey]}
                                onClick={() => handleUnshare(share.persona_id, share.user_id)}
                                className="text-destructive hover:text-destructive/90"
                              >
                                {unshareLoading[unshareKey] ? (
                                  <Spinner size="sm" />
                                ) : (
                                  <UserMinus className="w-4 h-4" />
                                )}
                              </Button>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* ── Share Persona Modal ── */}
      <Modal open={!!sharePersona} onOpenChange={(open) => !open && setSharePersona(null)}>
        <ModalContent size="md">
          <ModalHeader>
            <ModalTitle>
              Share "{sharePersona?.name}"
            </ModalTitle>
          </ModalHeader>
          <ModalBody>
            <p className="text-sm text-foreground mb-4">
              Select users to share this persona with. They will receive read-only access.
            </p>

            {sharePersona && (() => {
              const availableUsers = users.filter(
                (u) => !getAlreadySharedUserIds(sharePersona.id).has(u.id)
              )
              const allSelected = availableUsers.length > 0 && availableUsers.every((u) => selectedUserIds.has(u.id))

              const toggleSelectAll = () => {
                if (allSelected) {
                  setSelectedUserIds(new Set())
                } else {
                  setSelectedUserIds(new Set(availableUsers.map((u) => u.id)))
                }
              }

              return (
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {availableUsers.length > 0 && (
                    <>
                      <label
                        className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover:bg-muted border-b border-border mb-1"
                      >
                        <div
                          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                            allSelected
                              ? 'bg-primary border-primary text-primary-foreground'
                              : 'border-border'
                          }`}
                          onClick={toggleSelectAll}
                        >
                          {allSelected && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex-1 min-w-0" onClick={toggleSelectAll}>
                          <div className="text-sm font-semibold text-foreground">
                            All Users
                          </div>
                          <div className="text-xs text-muted-foreground">
                            Share with all {availableUsers.length} user{availableUsers.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <Badge variant="default">
                          <Users className="w-3 h-3 mr-1" />
                          {availableUsers.length}
                        </Badge>
                      </label>
                    </>
                  )}
                  {availableUsers.map((user) => (
                    <label
                      key={user.id}
                      className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer hover:bg-muted"
                    >
                      <div
                        className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                          selectedUserIds.has(user.id)
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'border-border'
                        }`}
                        onClick={() => toggleUserSelection(user.id)}
                      >
                        {selectedUserIds.has(user.id) && <Check className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0" onClick={() => toggleUserSelection(user.id)}>
                        <div className="text-sm font-medium text-foreground">
                          {user.display_name}
                        </div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                      <Badge variant={user.role === 'admin' ? 'warning' : 'secondary'}>
                        {user.role}
                      </Badge>
                    </label>
                  ))}
                  {availableUsers.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      This persona is already shared with all active users.
                    </p>
                  )}
                </div>
              )
            })()}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={() => setSharePersona(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleShare}
              disabled={selectedUserIds.size === 0}
              isLoading={isSharing}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Share with {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageFrame>
  )
}
